import { useCallback, useEffect, useRef } from 'react';
import { normalizeBusinessStorageId } from '../../../utils/unifiedDataHelpers';
import { executeBusinessFieldKvSync } from '../../../domain/persistence/businessFieldKvSyncOrchestrator';

/**
 * Partial KV → React sync for specific fields (Strangler extraction from DataContext).
 */
export function useBusinessFieldKvSync({ businessIdRef, setters, syncRef }) {
  const settersRef = useRef(setters);
  settersRef.current = setters;

  const syncBusinessFieldsFromKv = useCallback(
    (businessId, fields) => {
      const incoming = businessId != null ? String(businessId) : '';
      const current = businessIdRef.current != null ? String(businessIdRef.current) : '';
      const sameBusiness =
        incoming === current ||
        (incoming &&
          current &&
          normalizeBusinessStorageId(incoming) === normalizeBusinessStorageId(current));
      const bid = sameBusiness ? businessIdRef.current || businessId : null;
      if (!bid) return;

      // Entity-authority / deferred business_*_data: blob may be empty — field sync still
      // reads from entity_* via *ReadSource (parsedPayload is only a merge base).
      void (async () => {
        let parsedPayload = {};
        try {
          const { readBestBusinessMergedPayload } = await import('../../../utils/sharedCore');
          parsedPayload = readBestBusinessMergedPayload(bid)?.parsed || {};
        } catch {
          parsedPayload = {};
        }
        await executeBusinessFieldKvSync(bid, fields, {
          setters: settersRef.current,
          parsedPayload
        });
      })();
    },
    [businessIdRef]
  );

  useEffect(() => {
    if (syncRef) {
      syncRef.current = syncBusinessFieldsFromKv;
    }
  }, [syncBusinessFieldsFromKv, syncRef]);

  return { syncBusinessFieldsFromKv };
}
